import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Heading, Image, Text, useResponsive } from '@common-fe/common-fe';

import BannerImage from '@/assets/placeholders/dashboardBanner.svg';
import ROUTES from '@/common/routes';
import { Confetti } from '@/components/elements';
import { useBreadcrumbs } from '@/modules/core/hooks';
import useAllowNotifications from '@/modules/core/hooks/useAllowNotifications';
import NotificationList from '@/modules/notification/NotificationsList';
import { useAuthStore } from '@/modules/user/stores';
import { MODULE_NAME } from '@/modules/user/user.constants';
import { SEEN_KEY } from '@/utils/handlers/Token';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';
import { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

const isGustoTheme = THEMES.gusto === whiteLabelingConfig.title;

const Banner = () => {
  const { isMobile } = useResponsive();
  useBreadcrumbs([
    {
      key: ROUTES.HOME,
      title: 'Home',
      value: ROUTES.HOME,
    },
  ]);
  const { t } = useTranslation(MODULE_NAME);
  const { user } = useAuthStore();
  const isEmployee = useMemo(() => !!user?.employee?.id, [user]);
  const isNotificationsAllowed = useAllowNotifications();
  const alreadySaw = useRef(localStorage.getItem(SEEN_KEY));
  useEffect(() => {
    if (!alreadySaw.current) {
      localStorage.setItem(SEEN_KEY, 'true');
    }
  }, []);

  if (isMobile) {
    return (
      <Box
        overflow="hidden"
        style={{ position: 'relative' }}
        margin={{ top: 'spacing12' }}
        background="module"
        round="moduleRound"
        direction="column"
        height="400px"
      >
        <Box
          width={{ width: '100%' }}
          pad={{
            top: 'spacing32',
            horizontal: 'spacing16',
            bottom: '0px',
          }}
        >
          <Image
            // style={{ top: 0, bottom: 0, background: 'red', left: 0 }}
            className="image"
            src={whiteLabelingConfig?.images?.banner || BannerImage}
            fit="cover"
            width="100%"
            height="100%"
            style={{
              position: 'absolute',
              zIndex: 0,
              top: 0,
              right: 0,
              bottom: 0,
            }}
          />
          {isGustoTheme ? (
            <Box>
              <Text size="2xl" color="textTitle" weight={500} style={{ zIndex: 1 }}>
                {t('Welcome to the Benefits Card Hub')}
              </Text>
              <Text margin={{ top: 's' }} size="small" color="textSecondary">
                {t('Powered by Elevate')}
              </Text>
            </Box>
          ) : (
            <Box>
              <Text size="large" color="textTitle" style={{ zIndex: 1 }}>
                {t('Welcome, ')}
              </Text>
              <Heading
                level={3}
                size="medium"
                color="textTitle"
                style={{ zIndex: 1 }}
                data-testid="banner-username"
              >
                {user?.firstName}!
              </Heading>
            </Box>
          )}
        </Box>
        <Box>{isEmployee && isNotificationsAllowed && <NotificationList isMobileView />}</Box>
      </Box>
    );
  }

  return (
    <Box overflow="hidden" style={{ position: 'relative' }}>
      <Box
        direction="row"
        align="center"
        justify="between"
        height="258px"
        round="moduleRound"
        background="module"
        style={{
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        {isGustoTheme ? (
          <Box direction="row" align="center" width="500px" style={{ flexShrink: 0 }}>
            <Box fill pad={{ left: '40px' }}>
              <Text size="2xl" color="textTitle" weight={500} style={{ zIndex: 1 }}>
                {t('Welcome to the Benefits Card Hub')}
              </Text>
              <Text margin={{ top: 's' }} size="small" color="textSecondary">
                {t('Powered by Elevate')}
              </Text>
            </Box>
            <Image
              style={{ minWidth: '204px' }}
              src={whiteLabelingConfig?.images?.banner || BannerImage}
              fit="contain"
            />
          </Box>
        ) : (
          <Box
            width={{ max: '280px', width: '100%' }}
            justify="center"
            pad={{
              horizontal: 'spacing48',
              bottom: '0px',
            }}
            fill
          >
            <Image
              // style={{ top: 0, bottom: 0, background: 'red', left: 0 }}
              className="image"
              src={whiteLabelingConfig?.images?.banner || BannerImage}
              fit="contain"
              style={{
                position: 'absolute',
                zIndex: 0,
                minWidth: whiteLabelingConfig?.images?.isFullSizeBanner ? '100%' : undefined,
                left: 0,
                bottom: 0,
              }}
            />
            <Text size="large" color="textTitle" style={{ zIndex: 1 }}>
              {t('Welcome, ')}
            </Text>
            <Heading
              level={2}
              size="regular"
              color="textTitle"
              style={{ zIndex: 1 }}
              data-testid="banner-username"
            >
              {user?.firstName}!
            </Heading>
          </Box>
        )}

        <Box>{isEmployee && isNotificationsAllowed && <NotificationList />}</Box>
      </Box>

      {!alreadySaw.current && (
        <Box
          overflow="hidden"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <Confetti />
        </Box>
      )}
    </Box>
  );
};
export default Banner;
